import React, { useCallback, useEffect, useState } from "react";

import { Stack } from "@mui/material";
import { useFlags } from "launchdarkly-react-client-sdk";
import { FormProvider, useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom-v5-compat";

import {
  FullCustodian,
  fullCustodianOptions,
} from "@fartherfinance/frontend/api/Types";

import Drawer from "../../Drawer/Drawer";
import ButtonPrimary from "../../MUI/Button/Button";
import { useMonthList } from "../utils/useMonthList";
import useStatusNotification from "@src/multiCustodian/hooks/useStatusNotification";
import FlexWrapper from "@src/sharedComponents/Forms/FlexWrapper";
import FormDropdownField from "@src/sharedComponents/Forms/FormDropdownField";
import LogoLoadingStill from "@src/sharedComponents/LogoLoadingStill/LogoLoadingStill";

import styles from "./FAMBilling.module.css";

const MonthYear = "MonthYear";
const Custodian = "Custodian";
interface Form {
  [MonthYear]: { label: string; value: string } | undefined;
  [Custodian]: { label: FullCustodian } | undefined;
}

const FAMBilling = (): JSX.Element => {
  const navigate = useNavigate();
  const location = useLocation();

  const monthList = useMonthList();

  const [mutating, setMutating] = useState(false);

  const methods = useForm<Form>({
    mode: "all",
    reValidateMode: "onChange",
    defaultValues: {
      [MonthYear]: undefined,
      [Custodian]: undefined,
    },
  });

  const statusNotification = useStatusNotification();

  const { enableAdminFamBilling } = useFlags();

  useEffect(() => {
    if (!enableAdminFamBilling) {
      navigate("/Advisor/Clients", { state: location.state });
    }
  }, [enableAdminFamBilling, navigate, location]);

  const {
    getValues,
    formState: { isValid, isDirty },
  } = methods;

  const onClose = useCallback(
    () => navigate("/Advisor/Admin", { state: location.state }),
    [navigate, location]
  );

  const submit = async () => {
    if (mutating) {
      statusNotification("Form is being submitted", "Error");
      return;
    }

    if (!isDirty) {
      statusNotification("Nothing to submit", "Error");
      return;
    }

    if (!isValid) {
      statusNotification("Some fields are incomplete", "Error");
      return;
    }

    const values = getValues();

    if (values[MonthYear] === undefined) {
      statusNotification("Need to select a billing period", "Error");
      return;
    }

    if (values[Custodian] === undefined) {
      statusNotification("Need to select a custodian", "Error");
      return;
    }

    setMutating(true);

    // This functionality is currently being migrated
    statusNotification(
      "This functionality is currently not available. Please contact the development team for assistance.",
      "Error"
    );

    setMutating(false);
  };

  return (
    <Drawer
      isDrawerOpen
      onClose={onClose}
      footer={
        <Stack direction="row" gap="16px">
          <ButtonPrimary
            text="Cancel"
            variant="outlined"
            buttonType="primary"
            onClick={onClose}
          />

          <ButtonPrimary
            disabled={!isDirty || !isValid || mutating}
            text="Download CSV"
            variant="contained"
            buttonType="primary"
            onClick={submit}
          />
        </Stack>
      }
    >
      {mutating && <LogoLoadingStill onTop />}
      <FormProvider {...methods}>
        <div className={styles.title}>FAM Billing</div>

        <div className={styles.text}>
          Select the month and custodian for which we should compile billing
          records
        </div>

        <FlexWrapper>
          <FormDropdownField
            name={MonthYear}
            label={"Month"}
            values={monthList}
            disableSearch
            required={"Must not be empty"}
          />

          <FormDropdownField
            name={Custodian}
            label={"Custodian"}
            values={fullCustodianOptions.map((c) => ({ label: c }))}
            disableSearch
            required={"Must not be empty"}
          />
        </FlexWrapper>
      </FormProvider>
    </Drawer>
  );
};

export default FAMBilling;
